import { useMemo } from 'react';

import { HexIcon, Icons, Placeholder, tokens } from '@cast/design-system';
import { bytesToGiBiBytes } from '@cast/utils';

import { SegmentedMetricCard } from '../_components/SegmentedMetricCard';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { ActionRequired } from './_components/ActionRequired';
import { useGpuSummaryData } from './_hooks/useGpuSummaryData';

const TITLE = 'Total GPU memory waste';

const formatGiB = (value: number | undefined) =>
  value === undefined ? '--' : String(bytesToGiBiBytes(value).toFixed(2));

const REQUESTED_COLOR = tokens.theme.data.blue300;
const USED_COLOR = tokens.theme.data.green200;

export const GpuMemoryWasteCard = () => {
  const {
    data,
    isLoading,
    isCapabilitiesLoading,
    hasGpuMetrics,
    isDataProcessing,
    isGpuExporterInstalled,
    error,
    refetch,
  } = useGpuSummaryData();

  const segments = useMemo(
    () => [
      {
        label: 'Requested',
        value: data?.requestedBytes ?? 0,
        color: REQUESTED_COLOR,
        testId: 'gpu-memory-waste-requested',
      },
      {
        label: 'Used',
        value: data?.usedBytes ?? 0,
        color: USED_COLOR,
        testId: 'gpu-memory-waste-used',
      },
    ],
    [data]
  );

  if (!isCapabilitiesLoading && !isGpuExporterInstalled && !hasGpuMetrics) {
    return (
      <WidgetLayout
        title={TITLE}
        testId="gpu-memory-waste-card"
        headerDivider={false}
        slotProps={{ header: { py: 12 } }}
      >
        <ActionRequired />
      </WidgetLayout>
    );
  }

  if (!isCapabilitiesLoading && isDataProcessing) {
    return (
      <WidgetLayout
        title={TITLE}
        testId="gpu-memory-waste-card"
        headerDivider={false}
        slotProps={{ header: { py: 12 } }}
      >
        <Placeholder
          title="Data is being processed"
          body="For newly connected clusters, it might take up to 15 minutes for the data to be available."
          icon={
            <HexIcon
              variant="notification"
              icon={<Icons.HourglassHigh weight="fill" />}
              size={84}
            />
          }
        />
      </WidgetLayout>
    );
  }

  return (
    <SegmentedMetricCard
      title={TITLE}
      total={data?.wastedBytes}
      formatValue={formatGiB}
      unit="GiB"
      segments={segments}
      testId="gpu-memory-waste-card"
      metricTestId="gpu-memory-waste-total"
      barTestId="gpu-memory-waste-bar"
      isLoading={isLoading}
      error={error}
      refresh={refetch}
    />
  );
};
